import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NotificationService } from '@services/notification.service';
import { SidenavService } from '@services/sidenav.service';
import { SessionStorageService } from '@services/storage.service';
import { models } from 'powerbi-client';
import { PowerBIReportEmbedComponent } from 'powerbi-client-angular';
import { Subject, takeUntil } from 'rxjs';
import { EmbedconfigService, IEmbedConfig } from '../../shared/services/embedconfig.service';
import { IReport, ReportService } from '../../shared/services/report.service';

@Component({
	selector: 'app-reports',
	templateUrl: './reports.component.html',
	styleUrls: ['./reports.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ReportsComponent implements OnInit, OnDestroy {
	@ViewChild(PowerBIReportEmbedComponent)
	reportObj!: PowerBIReportEmbedComponent;

	reportConfig: IEmbedConfig = {
		id: null,
		groupId: null,
		type: 'report',
		embedUrl: undefined,
		tokenType: models.TokenType.Embed,
		accessToken: undefined,
		settings: undefined,
		expiration: undefined,
	};

	noReportSelected = true;
	extUrlReportSelected = false;
	reportCssClassName: string = 'powerbi-embed-class';

	reportEventHandlers = new Map([
		['loaded', () => console.log('report loaded')],
		['rendered', () => console.log('report rendered')],
		['error', (event) => console.log('report error', event)],
		['saved', (event) => this.handleSaveReport(event)],
	]);

	unsubscribe: Subject<void> = new Subject<void>();
	permissions: string[];
	currentReport: IReport;
	languageChanged: boolean = false;
	embedLang: string = this.sessionStorageService.get('User.PreferredLanguage');

	constructor(
		private reportService: ReportService,
		private embedService: EmbedconfigService,
		private sessionStorageService: SessionStorageService,
		private notificationService: NotificationService,
		public sidenavService: SidenavService,
		private sanitizer: DomSanitizer,
	) {
		document.addEventListener('webkitfullscreenchange', this.fullscreenChange);
		document.addEventListener('mozfullscreenchange', this.fullscreenChange);
		document.addEventListener('fullscreenchange', this.fullscreenChange);
		document.addEventListener('MSFullscreenChange', this.fullscreenChange);
	}

	fullscreenChange() {
		if (document.fullscreenEnabled) {
			console.log('enter fullscreen');
		} else {
			console.log('exit fullscreen');
		}
		// force to reload iframe once to prevent the iframe source didn't care about trying to resize the window
		// comment this line and you will see
		const iframe = document.querySelector('iframe');
		iframe.src = iframe.src.toString();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	ngOnInit(): void {
		this.sessionStorageService
			.watch('User.Permissions')
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((permissions) => (this.permissions = JSON.parse(permissions)));

		this.sessionStorageService.watch('User.PreferredLanguage').subscribe((newLanguage) => {
			if (newLanguage !== this.embedLang) {
				this.embedLang = newLanguage;
				this.languageChanged = true;
			}
		});

		this.embedService.embedConfig$.pipe(takeUntil(this.unsubscribe)).subscribe((embed) => {
			if (embed) {
				console.log('embedconfig updated');
				this.reportConfig = { ...embed };
				this.reportConfig.settings = {
					filterPaneEnabled: true,
					navContentPaneEnabled: true,
				};

				if (this.sidenavService.isPhone()) {
					this.reportConfig.viewMode = 0;
					this.reportConfig.settings = {
						filterPaneEnabled: false,
						navContentPaneEnabled: false,
					};
				}

				const thisReport = this.reportObj?.getReport();
				if (thisReport) {
					console.log('setting new accesstoken');
					void thisReport.setAccessToken(embed.accessToken);
				}

				const frame = this.getEmbedFrame();
				if (frame) {
					console.log('clearing powerbi iframe cache');
					frame.contentWindow.location.href = `${embed.embedUrl}&language=${this.sessionStorageService.get('User.PreferredLanguage')}`;
					// this.sessionStorageService.watch('User.PreferredLanguage').subscribe(NL => {
					//   let frame = this.getEmbedFrame();
					//   frame.contentWindow.location.href = embed.embedUrl + `&language=${NL}&formatLocale=${NL}`;
					// });
				}
			}
		});

		this.reportService.report$.pipe(takeUntil(this.unsubscribe)).subscribe((x) => {
			this.currentReport = x;
			x !== null ? (this.noReportSelected = false) : (this.noReportSelected = true);
			x !== null && !x.groupId ? (this.extUrlReportSelected = true) : (this.extUrlReportSelected = false);
		});
	}

	toggleFullscreen = (isExtResource: boolean) => {
		if (isExtResource) {
			console.log('setting extResource iframe to fullscreen');
			// check if fullscreen mode is available
			if (document.fullscreenEnabled) {
				// which element will be fullscreen
				const iframe = document.querySelector('#container iframe');
				// Do fullscreen
				if (iframe.requestFullscreen) {
					void iframe.requestFullscreen();
				}
			}
		} else {
			this.reportObj.getReport().fullscreen();
		}
	};

	safeUrl(link: string): SafeResourceUrl {
		return this.sanitizer.bypassSecurityTrustResourceUrl(link);
	}

	getEmbedFrame = () => this.reportObj?.getReport()?.element?.firstChild as HTMLIFrameElement;

	handleSaveReport(saveEvent: CustomEvent) {
		const saveAs = saveEvent.detail.saveAs;
		const isAdmin = this.permissions.findIndex((x) => x === 'insights:ReportCreate') > 0;
		this.reportService.synchronizeWithPowerBI('REPORT', isAdmin, saveAs);

		let reportName;
		if (saveAs) {
			reportName = saveEvent.detail.reportName;
		} else {
			reportName = this.currentReport.name;
		}

		this.notificationService.notify(['insights.report.savednotification_heading'], ['insights.report.savednotification_text', { reportName }], 'success');

		if (saveAs) {
			const saveAsPBIReportId = saveEvent.detail.reportObjectId;
			this.sessionStorageService.set('Insights.SaveAsReportId', saveAsPBIReportId);
		}
	}
}
